import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {APIListWrapper} from "../models/common/api-list-wrapper.model";
import {ContractRevisions} from "./models/contract-revisions.model";
import {Contracts} from "./models/contracts.model";
import {DeliveryRateCard} from "./models/delivery-rate-card.model";
import {environment} from "../../environments/environment";
import {Company} from "../company/company.model";
import {ContractCrmContext} from "./models/contract-crm-context.model";
import { QuotationContractLink } from "../crm/quotations/quotation-contract-conversion.model";
import {
    ContractPricingDecisionRequest,
    ContractPricingDecisionResponse
} from "./models/contract-pricing-decision.model";
import {
    ContractDocumentPreview,
    ContractRevisionSection,
    ContractTemplate,
    ContractTemplateSection,
    GenerateContractDocumentRequest,
    GeneratedContractDocument,
    SignedDocumentRequest
} from "./models/contract-drafting.model";
import {
    ContractLifecycleState,
    ContractLifecycleDashboardSummary,
    ContractObligation,
    ContractRenewal,
    ContractTransitionRequest,
    ContractTransitionResult
} from "./models/contract-lifecycle.model";



@Injectable({
    providedIn: 'root'
})
export class ContractsService {

    private baseUrl = `${environment.restApiUrl}contracts`; // Adjust the base URL if needed

    constructor(private http: HttpClient) { }

    private sanitizeRevisionPayload(revision: ContractRevisions): ContractRevisions {
        const payload = { ...(revision as unknown as Record<string, unknown>) };
        delete payload['deliveryRateCards'];
        return payload as unknown as ContractRevisions;
    }

    // ---------- Contract CRUD Operations ----------

    getAllContracts(page: number, pageSize: number, firstPartyId?: number, secondPartyId?: number, crmClientId?: number, crmAccountId?: number, sourceQuotationId?: number): Observable<APIListWrapper<Contracts>> {
        let reqParams = new HttpParams()
            .set('page', String(page))
            .set('pageSize', String(pageSize));
        if (firstPartyId !== undefined) reqParams = reqParams.set('firstPartyId', String(firstPartyId));
        if (secondPartyId !== undefined) reqParams = reqParams.set('secondPartyId', String(secondPartyId));
        if (crmClientId !== undefined) reqParams = reqParams.set('crmClientId', String(crmClientId));
        if (crmAccountId !== undefined) reqParams = reqParams.set('crmAccountId', String(crmAccountId));
        if (sourceQuotationId !== undefined) reqParams = reqParams.set('sourceQuotationId', String(sourceQuotationId));
        return this.http.get<APIListWrapper<Contracts>>(this.baseUrl, { params: reqParams });
    }

    getContractRelatedCompanies(companyId: number): Observable<Company[]> {
        const url = `${this.baseUrl}/companies?companyId=${companyId}`;
        return this.http.get<Company[]>(url);
    }

    getContractById(id: number): Observable<Contracts> {
        return this.http.get<Contracts>(`${this.baseUrl}/${id}`);
    }

    getCrmContextForClient(clientId: number, companyId?: number): Observable<ContractCrmContext> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<ContractCrmContext>(`${this.baseUrl}/context/crm-client/${clientId}`, { params });
    }

    getCrmContextForContract(contractId: number, companyId?: number): Observable<ContractCrmContext> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<ContractCrmContext>(`${this.baseUrl}/${contractId}/crm-context`, { params });
    }

    getSourceQuotations(contractId: number, companyId?: number): Observable<QuotationContractLink[]> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<QuotationContractLink[]>(`${this.baseUrl}/${contractId}/source-quotations`, { params });
    }

    getRevisionSections(contractId: number, revisionId: number): Observable<ContractRevisionSection[]> {
        return this.http.get<ContractRevisionSection[]>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/sections`);
    }

    saveRevisionSections(contractId: number, revisionId: number, sections: ContractRevisionSection[]): Observable<ContractRevisionSection[]> {
        return this.http.put<ContractRevisionSection[]>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/sections`, sections);
    }

    initializeRevisionSections(contractId: number, revisionId: number, companyId?: number, actorId?: number): Observable<ContractRevisionSection[]> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        if (actorId !== undefined) params = params.set('actorId', String(actorId));
        return this.http.post<ContractRevisionSection[]>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/sections/initialize`, {}, { params });
    }

    getContractTemplates(companyId?: number, active?: boolean): Observable<ContractTemplate[]> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        if (active !== undefined) params = params.set('active', String(active));
        return this.http.get<ContractTemplate[]>(`${this.baseUrl}/templates`, { params });
    }

    saveContractTemplate(template: ContractTemplate): Observable<ContractTemplate> {
        if (template.templateId) {
            return this.http.put<ContractTemplate>(`${this.baseUrl}/templates/${template.templateId}`, template);
        }
        return this.http.post<ContractTemplate>(`${this.baseUrl}/templates`, template);
    }

    getContractTemplateSections(templateId: number): Observable<ContractTemplateSection[]> {
        return this.http.get<ContractTemplateSection[]>(`${this.baseUrl}/templates/${templateId}/sections`);
    }

    saveContractTemplateSections(templateId: number, sections: ContractTemplateSection[]): Observable<ContractTemplateSection[]> {
        return this.http.put<ContractTemplateSection[]>(`${this.baseUrl}/templates/${templateId}/sections`, sections);
    }

    getDocumentPreview(contractId: number, revisionId: number, companyId?: number): Observable<ContractDocumentPreview> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<ContractDocumentPreview>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/document-preview`, { params });
    }

    generateContractDocument(contractId: number, revisionId: number, request: GenerateContractDocumentRequest): Observable<GeneratedContractDocument> {
        return this.http.post<GeneratedContractDocument>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents/generate`, request);
    }

    getGeneratedContractDocuments(contractId: number, revisionId: number): Observable<GeneratedContractDocument[]> {
        return this.http.get<GeneratedContractDocument[]>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents`);
    }

    approveGeneratedContractDocument(contractId: number, revisionId: number, documentId: number, actorId?: number): Observable<GeneratedContractDocument> {
        let params = new HttpParams();
        if (actorId !== undefined) params = params.set('actorId', String(actorId));
        return this.http.post<GeneratedContractDocument>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents/${documentId}/approve`, {}, { params });
    }

    saveSignedDocumentUrl(contractId: number, revisionId: number, documentId: number, request: SignedDocumentRequest): Observable<GeneratedContractDocument> {
        return this.http.post<GeneratedContractDocument>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents/${documentId}/signed-url`, request);
    }

    createContractPdfLink(contractId: number, revisionId: number, documentId: number): Observable<GeneratedContractDocument> {
        return this.http.post<GeneratedContractDocument>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents/${documentId}/pdf-link`, {});
    }

    downloadContractPdf(contractId: number, revisionId: number, documentId: number): Observable<Blob> {
        return this.http.post(`${this.baseUrl}/${contractId}/revisions/${revisionId}/documents/${documentId}/pdf-download`, {}, { responseType: 'blob' });
    }

    getRevisionLifecycle(contractId: number, revisionId: number, companyId?: number): Observable<ContractLifecycleState> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<ContractLifecycleState>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/lifecycle`, { params });
    }

    transitionRevision(contractId: number, revisionId: number, request: ContractTransitionRequest): Observable<ContractTransitionResult> {
        return this.http.post<ContractTransitionResult>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/transition`, request);
    }

    saveContractRenewal(contractId: number, revisionId: number, renewal: ContractRenewal): Observable<ContractRenewal> {
        return this.http.put<ContractRenewal>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/renewal`, renewal);
    }

    saveContractObligation(contractId: number, revisionId: number, obligation: ContractObligation): Observable<ContractObligation> {
        return this.http.post<ContractObligation>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/obligations`, obligation);
    }

    getLifecycleDashboard(companyId?: number): Observable<ContractLifecycleDashboardSummary> {
        let params = new HttpParams();
        if (companyId !== undefined) params = params.set('companyId', String(companyId));
        return this.http.get<ContractLifecycleDashboardSummary>(`${this.baseUrl}/lifecycle-dashboard`, { params });
    }

    createContract(contract: Contracts): Observable<Contracts> {
        return this.http.post<Contracts>(this.baseUrl, contract);
    }

    updateContract(id: number, contract: Contracts): Observable<Contracts> {
        return this.http.put<Contracts>(`${this.baseUrl}/${id}`, contract);
    }

    deleteContract(id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}`);
    }

    // ---------- Contract Revision CRUD Operations ----------

    getAllRevisionsForContract(contractId: number): Observable<ContractRevisions[]> {
        return this.http.get<ContractRevisions[]>(`${this.baseUrl}/${contractId}/revisions`);
    }

    getRevisionById(contractId: number, revisionId: number): Observable<ContractRevisions> {
        return this.http.get<ContractRevisions>(`${this.baseUrl}/${contractId}/revisions/${revisionId}`);
    }

    createRevision(contractId: number, revision: ContractRevisions): Observable<ContractRevisions> {
        return this.http.post<ContractRevisions>(`${this.baseUrl}/${contractId}/revisions`, this.sanitizeRevisionPayload(revision));
    }

    updateRevision(contractId: number, revisionId: number, revision: ContractRevisions): Observable<ContractRevisions> {
        return this.http.put<ContractRevisions>(`${this.baseUrl}/${contractId}/revisions/${revisionId}`, this.sanitizeRevisionPayload(revision));
    }

    deleteRevision(contractId: number, revisionId: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${contractId}/revisions/${revisionId}`);
    }

    // ---------- Delivery Rate Card Operations ----------

    getDeliveryRateCardsByRevisionId(contractId: number, revisionId: number): Observable<DeliveryRateCard[]> {
        return this.http.get<DeliveryRateCard[]>(`${this.baseUrl}/${contractId}/revisions/${revisionId}/delivery-rate-cards`);
    }

    createDeliveryRateCardsForRevision(contractId: number, revisionId: number, deliveryRateCards: DeliveryRateCard[]): Observable<DeliveryRateCard[]> {
        return this.http.post<DeliveryRateCard[]>(
            `${this.baseUrl}/${contractId}/revisions/${revisionId}/delivery-rate-cards`,
            deliveryRateCards
        );
    }

    getDeliveryRateCardByRateId(rateId: number): Observable<DeliveryRateCard> {
        const url = `${this.baseUrl}/delivery-rate-cards/${rateId}`;
        return this.http.get<DeliveryRateCard>(url);
    }

    getRateCard(companyId: number, sourceId: number, destinationId: number, vehicleTypeId?: number): Observable<DeliveryRateCard> {
        const url = `${this.baseUrl}/rate-card?companyId=${companyId}&sourceId=${sourceId}&destinationId=${destinationId}&vehicleTypeId=${vehicleTypeId}`;
        return this.http.get<DeliveryRateCard>(url);
    }

    decideRateCard(request: ContractPricingDecisionRequest): Observable<ContractPricingDecisionResponse> {
        return this.http.post<ContractPricingDecisionResponse>(`${this.baseUrl}/rate-card/decision`, request);
    }
}
